import React from 'react';
import syncIcon from '../assets/images/sync-icon-green.svg';
import loadingIcon from '../assets/images/loadingIcon.svg';
import errorIcon from '../assets/images/sync-error-icon.svg';

interface StatusConfigItem {
  backgroundColor: string;
  color: string;
  icon: string;
  /** Glyph colour when it differs from the label colour. */
  iconColor?: string;
}

export const statusConfig: Record<string, StatusConfigItem> = {
  Synced: {
    backgroundColor: 'var(--thin-green)',
    color: 'var(--green-900)',
    icon: syncIcon,
  },
  'Basic Tracking': {
    backgroundColor: 'var(--thin-green)',
    color: 'var(--green-900)',
    icon: '', // Change if you have a better icon
  },
  'Checking Credentials': {
    backgroundColor: 'var(--light-gray)',
    color: 'var(--text-secondary)',
    icon: loadingIcon,
  },
  'Incorrect Credentials': {
    backgroundColor: 'var(--danger-thin)',
    color: 'var(--danger)',
    icon: errorIcon,
  },
  // A credential whose last verification failed (customer_credentials ERROR).
  Failed: {
    backgroundColor: 'var(--danger-thin)',
    color: 'var(--danger)',
    icon: errorIcon,
  },
  Default: {
    backgroundColor: 'var(--light-gray)',
    color: 'var(--text-primary)',
    icon: loadingIcon,
    iconColor: 'var(--text-secondary)',
  },
};

interface StatusTagProps {
  status: string;
  /** Native hover text, e.g. the reason a credential failed verification. */
  title?: string;
}

const StatusTag: React.FC<StatusTagProps> = ({ status, title }) => {
  // Normalize the incoming status
  let mappedStatus = status;
  if (status === 'Connected' || status === 'linked') mappedStatus = 'Synced';
  else if (status === 'error') mappedStatus = 'Incorrect Credentials';
  else if (status === 'basic') mappedStatus = 'Basic Tracking';
  else if (status === 'pending') mappedStatus = 'Checking Credentials';
  // customer_credentials.Credential.Status values (Settings > Credentials).
  else if (status === 'SYNCED') mappedStatus = 'Synced';
  else if (status === 'ERROR') mappedStatus = 'Failed';
  else if (status === 'PENDING') mappedStatus = 'Checking Credentials';

  // Pick from config or fallback
  const { backgroundColor, color, icon, iconColor } =
    statusConfig[mappedStatus] || statusConfig['Checking Credentials'];

  return (
    <span
      title={title || undefined}
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: '6px',
        padding: '4px 12px',
        borderRadius: '4px',
        backgroundColor,
        color,
        fontSize: '10px',
        fontWeight: '600',
        width: 'max-content',
      }}
    >
      {icon && (
        // The glyph is a single-colour SVG; painting it as a mask lets it take
        // the theme-aware label colour instead of its baked-in light-theme fill.
        <span
          role='img'
          aria-label={mappedStatus}
          style={{
            width: '16px',
            height: '16px',
            backgroundColor: iconColor ?? color,
            // Quoted on purpose: in a build, Vite inlines small SVGs as
            // data:image/svg+xml URLs containing single quotes, which an
            // unquoted url() can't hold, so the whole mask would be dropped.
            WebkitMaskImage: `url("${icon}")`,
            maskImage: `url("${icon}")`,
            // Prefixed copies for browsers that only support -webkit-mask.
            WebkitMaskRepeat: 'no-repeat',
            maskRepeat: 'no-repeat',
            WebkitMaskPosition: 'center',
            maskPosition: 'center',
            WebkitMaskSize: 'contain',
            maskSize: 'contain',
          }}
        />
      )}
      {mappedStatus}
    </span>
  );
};

export default StatusTag;
